import { createInitialLastInsertedField, useSigningState } from '@modusign/react-signing/setup';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useUnmount } from 'react-use';

import withAuthentication from '@app/containers/withAuthentication';
import { checkIsInOrder } from '@app/features/apply-react-signing/libs/checkIsInOrder';
import { toSigningStateForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { TemplateEditorExitPathContextProvider } from '@app/features/template/context/TemplateEditorExitPathContext';
import useLocationExitPath from '@app/hooks/useLocationExitPath';
import { usePDF } from '@app/hooks/usePDF';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import { initializeDocumentRenderTree } from '@app/store/document/renderTree/actions';
import { clearDocumentSetupState } from '@app/store/documentSetup/actions';
import { templateClearAllState, templateDataFetched } from '@app/store/template/actions';
import { getFileFromURL } from '@app/store/transport-layer/files';
import { getTemplateById } from '@app/store/transport-layer/template';
import { copyTemplateRequesterAttachments } from '@app/store/transport-layer/template/templates/attachments/copy';

interface TemplateEditionLayoutComponentProps {
  children: React.ReactNode;
}

function TemplateEditionLayoutComponent({ children }: TemplateEditionLayoutComponentProps) {
  const dispatch = useDispatch();
  const [isLoaded, setIsLoaded] = useState(false);
  const { initialize } = useSigningState();

  const { exitPath } = useLocationExitPath();

  const { id } = useParams<{ id: string }>();
  const workspaceId = useWorkspaceId();
  const { setPDF, resetPDF } = usePDF();

  useEffect(() => {
    initializeOnMount();

    async function initializeOnMount() {
      if (id && workspaceId) {
        resetPDF();
        const data = await getTemplateById(workspaceId, id);
        const copiedAttachmentsResponse = await copyTemplateRequesterAttachments(workspaceId, id);
        if (id) {
          dispatch(
            templateDataFetched({
              originalTitle: data.title,
              id: data.id,
              labelIds: data.labelIds,
            })
          );

          const { data: pdf } = await getFileFromURL(data.file.url);
          setPDF(pdf);

          const converted = toSigningStateForSetup(
            copiedAttachmentsResponse.attachments,
            data.participants,
            data.fields,
            data.fieldGroups,
            data.verifications
          );
          initialize({
            attachments: converted.attachments,
            participants: converted.participants,
            fields: converted.fields,
            verifications: converted.verifications,
            document: {
              pdfFile: {
                url: data.file.url,
              },
              pdfPassword: null,
              title: data.title,
            },
            ui: {
              isInOrder: checkIsInOrder(converted.participants),
              insertingField: null,
              lastInsertedField: createInitialLastInsertedField(),
            },
          });
        }
        setIsLoaded(true);
      }
    }

    return () => {
      initialize();
      resetPDF();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, id, workspaceId]);

  useUnmount(() => {
    resetPDF();
    initialize();
    dispatch(clearDocumentSetupState());
    dispatch(templateClearAllState());
    dispatch(initializeDocumentRenderTree());
  });

  // TODO(@DevSDK): maybe we need loading spinner?
  return isLoaded ? (
    <TemplateEditorExitPathContextProvider exitPath={exitPath}>
      <div className="Background Background--dark-gray">{children}</div>
    </TemplateEditorExitPathContextProvider>
  ) : null;
}

export default withAuthentication(TemplateEditionLayoutComponent);
